import {
  fromBusinessLocationDtoToBusinessLocation,
  fromCreateBusinessLocationRequestToCreateBusinessLocationDto,
  fromUpdateBusinessLocationRequestToUpdateBusinessLocationDto,
  type BusinessLocation,
  type CreateBusinessLocationRequest,
  type UpdateBusinessLocationRequest,
} from "@/features/business/types/businessLocationTypes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessLocationApiService } from "../services/businessLocationApiService";

const staleTime = 30 * 60 * 1000; // 30 minutes

export const locationKeys = {
  all: ["location"] as const,
  list: (businessId?: string) => businessId
    ? [...locationKeys.all, "list", businessId] as const
    : [...locationKeys.all, "list"] as const,
  detail: (id: string) => [...locationKeys.all, "detail", id] as const,
};

export const useBusinessLocations = (businessId: string) => {
  return useQuery<BusinessLocation[], Error>({
    queryKey: locationKeys.list(businessId),
    queryFn: async () => {
      const data = await businessLocationApiService.getAll(businessId);
      return data.map(fromBusinessLocationDtoToBusinessLocation);
    },
    enabled: !!businessId,
    staleTime: staleTime,
  });
};

export const useBusinessLocation = (id: string) => {
  return useQuery<BusinessLocation, Error>({
    queryKey: locationKeys.detail(id),
    queryFn: async () => {
      const dto = await businessLocationApiService.getById(id);
      return fromBusinessLocationDtoToBusinessLocation(dto);
    },
    enabled: !!id,
    staleTime: staleTime,
  });
};

export const useCreateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation<BusinessLocation, Error, CreateBusinessLocationRequest>({
    mutationFn: async (data: CreateBusinessLocationRequest) => {
      try {
        const dto =
          fromCreateBusinessLocationRequestToCreateBusinessLocationDto(data);

        const resultDto = await businessLocationApiService.create(dto);
        return fromBusinessLocationDtoToBusinessLocation(resultDto);
      } catch (e) {
        console.error("Error creating location:", e);
        throw e;
      }
    },
    onSuccess: (location) => {
      queryClient.setQueryData(locationKeys.detail(location.id), location);
      queryClient.setQueryData<BusinessLocation[]>(locationKeys.list(location.businessId), (previous) =>
        previous ? [...previous.filter((item) => item.id !== location.id), location] : undefined);
      return queryClient.invalidateQueries({ queryKey: locationKeys.list(location.businessId) });
    },
  });
};

export const useUpdateLocation = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    BusinessLocation,
    Error,
    Partial<UpdateBusinessLocationRequest>
  >({
    mutationFn: async (data: Partial<UpdateBusinessLocationRequest>) => {
      const dto =
        fromUpdateBusinessLocationRequestToUpdateBusinessLocationDto(data);
      const resultDto = await businessLocationApiService.update(id, dto);
      return fromBusinessLocationDtoToBusinessLocation(resultDto);
    },
    onSuccess: (location) => {
      queryClient.setQueryData(locationKeys.detail(id), location);
      queryClient.setQueryData<BusinessLocation[]>(locationKeys.list(location.businessId), (previous) =>
        previous?.map((item) => item.id === location.id ? location : item));
      return queryClient.invalidateQueries({ queryKey: locationKeys.list(location.businessId) });
    },
  });
};

export const useDeleteLocation = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error>({
    mutationFn: () => businessLocationApiService.delete(id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: locationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: locationKeys.list() });
    },
  });
};
